import { Fragment, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, Trash2 } from 'lucide-react'
import {
  listRules, setDefaultRule, setOverrideRule, deleteOverrideRule,
  hardenAmazonLivePayload, isAmazonLiveRule,
  AMAZON_DIRECT_PUSH_ADAPTER, AMAZON_LIVE_GATES_HINT,
  PRICE_ROLLOUT_PORTALS,
  type RolloutRule, type RolloutStatus, type SetRulePayload, type RolloutMode, type Betrieb,
} from '@/api/priceRollout'
import { getStrategyExceptions, setStrategy, channelPreview } from '@/api/pricing'
import { ApiError } from '@/api/client'
import { Panel } from '@/components/Panel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { portalText, regelKlartext, regelSchreibziel, totbandKlartext, schrittKlartext, taktKlartext, pauseKlartext } from '@/lib/rollout-klartext'
import { useRolloutStatus } from './useRolloutStatus'
import { KontoAufschlagPanel } from './KontoAufschlagPanel'

const PORTALS = PRICE_ROLLOUT_PORTALS

const DEFAULT_PAYLOAD: SetRulePayload = {
  account: null, max_step_ct: 99, max_step_pct: null, interval_days: 25, cooldown_days: 7,
  dead_band_ct: 5, dead_band_pct: 0.01, ceiling_pct_of_rrp: null, min_price: null,
  dr_propagates: true, direct_push_adapter: null, is_enabled: false, mode: 'dry_run',
}

function toPayload(r: RolloutRule): SetRulePayload {
  return {
    account: r.account, max_step_ct: r.max_step_ct, max_step_pct: r.max_step_pct,
    interval_days: r.interval_days, cooldown_days: r.cooldown_days,
    dead_band_ct: r.dead_band_ct, dead_band_pct: r.dead_band_pct,
    ceiling_pct_of_rrp: r.ceiling_pct_of_rrp, min_price: r.min_price,
    dr_propagates: r.dr_propagates,
    direct_push_adapter: (r.direct_push_adapter as SetRulePayload['direct_push_adapter']) ?? null,
    is_enabled: r.is_enabled, mode: r.mode,
  }
}

function ModeBadge({ rule }: { rule: RolloutRule }) {
  if (rule.mode === 'live') {
    return <span className="px-1.5 py-0.5 rounded bg-coral/20 text-coral text-[10px] font-bold">LIVE</span>
  }
  return <span className="px-1.5 py-0.5 rounded bg-panel-2 text-muted text-[10px] font-semibold">dry_run</span>
}

/** Direct-Push sichtbar machen: bei amazon_spapi schreibt nicht DreamRobot, sondern die SP-API. */
function AdapterBadge({ rule }: { rule: RolloutRule }) {
  if (rule.direct_push_adapter !== AMAZON_DIRECT_PUSH_ADAPTER) return null
  return (
    <span
      title="Direct-Push über Amazon SP-API — DreamRobot-Amazon-Preis und -Preisdokumentation sind gesperrt"
      className="ml-1 px-1.5 py-0.5 rounded bg-panel-2 text-[10px] font-mono"
    >
      SP-API
    </span>
  )
}

const MAX_BETROFFEN = 10

/** Betroffene Rollouts für die LIVE-Rückfrage: Zahl aktiv/pausiert und Sternnummern (max. 10 + „weitere“). */
function betroffenText(rows: readonly RolloutStatus[]): string {
  if (rows.length === 0) return 'Betroffene Rollouts: keine.'
  const aktiv = rows.filter((r) => r.status === 'active').length
  const pausiert = rows.filter((r) => r.status === 'paused').length
  const namen = rows.slice(0, MAX_BETROFFEN).map((r) => r.kennung?.stern ?? r.kennung?.sku ?? r.sku ?? String(r.stock_item_id))
  const rest = rows.length - namen.length
  return `Betroffene Rollouts: ${rows.length} (${aktiv} aktiv, ${pausiert} pausiert) – ${namen.join(', ')}${rest > 0 ? ` und ${rest} weitere` : ''}.`
}

/** LIVE-Rückfrage mit echtem Schreibziel des Portals (gleiche Wahl wie der Tick) und den betroffenen Rollouts. */
function liveFrage(portal: string, form: SetRulePayload, betroffen: readonly RolloutStatus[]): string {
  const ziel = regelSchreibziel(portal, isAmazonLiveRule(portal, form.mode) ? AMAZON_DIRECT_PUSH_ADAPTER : form.direct_push_adapter ?? null)
  const kopf = isAmazonLiveRule(portal, form.mode)
    ? [
        `Modus LIVE für Amazon schreibt echte Preise direkt über die Amazon SP-API (${AMAZON_DIRECT_PUSH_ADAPTER}).`,
        'DreamRobot erhält dabei KEINE Preis-Autorität (dr_propagates=false).',
        AMAZON_LIVE_GATES_HINT,
      ]
    : [`Modus LIVE schreibt echte Preise. Schreibziel: ${ziel}${ziel.startsWith('DR-') ? ' (über DreamRobot)' : ''}.`]
  return [...kopf, betroffenText(betroffen), 'Wirklich aktivieren?'].join('\n')
}

/** Regel-Formular. Dead-Band bewusst nicht editierbar (Backend unverdrahtet) — Werte werden durchgereicht. */
function RuleForm({ portal, initial, onSave, saving, isNew = false, betroffen = [] }: {
  portal: string
  initial: SetRulePayload
  onSave: (p: SetRulePayload) => void
  saving: boolean
  isNew?: boolean
  /** Rollouts, die diese Regel steuert (für die LIVE-Rückfrage). */
  betroffen?: readonly RolloutStatus[]
}) {
  const [form, setForm] = useState<SetRulePayload>(initial)
  const set = <K extends keyof SetRulePayload>(k: K, v: SetRulePayload[K]) => setForm((f) => ({ ...f, [k]: v }))
  const numOrNull = (s: string) => (s.trim() === '' ? null : Number(s))
  const amazonLive = isAmazonLiveRule(portal, form.mode)

  function submit() {
    const enablingStoredLiveRule = !initial.is_enabled && form.is_enabled
    if (form.mode === 'live' && (isNew || initial.mode !== 'live' || enablingStoredLiveRule)) {
      if (!window.confirm(liveFrage(portal, form, betroffen))) return
    }
    // Amazon-LIVE wird hier bereits normalisiert (die API-Schicht tut es fail-safe nochmals).
    onSave(hardenAmazonLivePayload(portal, form))
  }

  return (
    <div className="border border-primary/40 rounded-md p-4 my-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
      <div className="col-span-2 sm:col-span-4">
        <h3 className="font-semibold">Tempo</h3>
        <p className="text-xs text-muted">Wie weit und wie oft der Preis Richtung Ziel geht.</p>
      </div>
      <label>max. Schritt (ct)
        <Input type="number" value={form.max_step_ct}
          onChange={(e) => set('max_step_ct', Number(e.target.value))} className="h-8 mt-1" />
      </label>
      <label>max. Schritt (%, leer = aus)
        <Input type="number" value={form.max_step_pct == null ? '' : Math.round(form.max_step_pct * 10000) / 100}
          onChange={(e) => set('max_step_pct', e.target.value.trim() === '' ? null : Number(e.target.value) / 100)}
          className="h-8 mt-1" />
      </label>
      <label>Takt (Tage)
        <Input type="number" value={form.interval_days}
          onChange={(e) => set('interval_days', Number(e.target.value))} className="h-8 mt-1" />
      </label>
      <label>Sperre nach Schritt (Tage)
        <Input type="number" value={form.cooldown_days}
          onChange={(e) => set('cooldown_days', Number(e.target.value))} className="h-8 mt-1" />
      </label>
      <div className="col-span-2 sm:col-span-4">
        <h3 className="font-semibold">Grenzen</h3>
        <p className="text-xs text-muted">Unter- und Obergrenze des Preises; kleinere Zieländerungen als das Dead-Band werden ignoriert.</p>
      </div>
      <label>Mindestpreis (€, leer = keiner)
        <Input type="number" step="0.01" value={form.min_price ?? ''}
          onChange={(e) => set('min_price', numOrNull(e.target.value))} className="h-8 mt-1" />
      </label>
      <label>Ceiling (Faktor × UVP, z.B. 1,1; leer = aus)
        <Input type="number" step="0.05" value={form.ceiling_pct_of_rrp ?? ''}
          onChange={(e) => set('ceiling_pct_of_rrp', numOrNull(e.target.value))} className="h-8 mt-1" />
      </label>
      <p className="col-span-2 sm:col-span-4 text-xs text-ink-2">
        {totbandKlartext(form.dead_band_ct, form.dead_band_pct)}.
      </p>
      <div className="col-span-2 sm:col-span-4">
        <h3 className="font-semibold">Schreiben</h3>
        <p className="text-xs text-muted">Ob die Regel läuft und ob sie echte Preise schreibt.</p>
      </div>
      <label className="flex items-center gap-2 col-span-2">
        <input type="checkbox" checked={form.is_enabled} onChange={(e) => set('is_enabled', e.target.checked)} />
        Aktiv
      </label>
      <label className="col-span-2">
        Modus
        <select value={form.mode} onChange={(e) => set('mode', e.target.value as RolloutMode)}
          className="block w-full h-8 px-2 border border-line rounded bg-bg mt-1">
          <option value="dry_run">nur Protokoll (dry_run)</option>
          <option value="live">
            {portal === 'amazon' ? 'LIVE (Direct-Push über Amazon SP-API)' : `LIVE (schreibt echte Preise: ${regelSchreibziel(portal, form.direct_push_adapter ?? null)})`}
          </option>
        </select>
      </label>
      {amazonLive && (
        <div className="col-span-2 sm:col-span-4 rounded border border-warn/40 bg-warn/10 p-2 text-xs text-warn space-y-1">
          <div className="font-semibold">Amazon LIVE = Direct-Push über die SP-API</div>
          <div className="font-mono text-[11px]">
            direct_push_adapter=amazon_spapi · dr_propagates=false
          </div>
          <div>DreamRobot wird für Amazon weder als Preisautor noch zur Preisdokumentation aufgerufen.</div>
          <div>{AMAZON_LIVE_GATES_HINT}</div>
        </div>
      )}
      <p className="col-span-2 sm:col-span-4 text-xs text-ink-2">
        {regelKlartext(form)}.
      </p>
      <div className="col-span-2 sm:col-span-4 flex justify-end">
        <Button size="sm" onClick={submit} disabled={saving}>Speichern</Button>
      </div>
    </div>
  )
}

function ruleSummary(r: RolloutRule): string {
  const min = r.min_price == null ? '' : ` · Mindestpreis ${r.min_price.toFixed(2).replace('.', ',')} €`
  return `${regelKlartext(r)}${min}`
}

const offenerRollout = (r: RolloutStatus) => r.status !== 'done'
const gleicheSku = (a: string | null | undefined, b: string) => {
  if (!a) return false
  const x = a.toLowerCase(); const y = b.trim().toLowerCase()
  return x === y || x === `dr-${y}`
}

export function RulesTab() {
  const queryClient = useQueryClient()
  const { data: rules = [], isLoading } = useQuery({ queryKey: ['rollout-rules'], queryFn: listRules })
  const { data: exceptions = [] } = useQuery({ queryKey: ['strategy-exceptions'], queryFn: getStrategyExceptions })
  const [editing, setEditing] = useState<number | null>(null)
  const [showNewOverride, setShowNewOverride] = useState(false)
  const [newSku, setNewSku] = useState('')
  const [newPortal, setNewPortal] = useState<string>('base')

  const defaults = rules.filter((r) => r.stock_item_id === null)
  const overrides = rules.filter((r) => r.stock_item_id !== null)
  const { data: status } = useRolloutStatus()
  const offen = (status?.rollouts ?? []).filter(offenerRollout)
  // Portal-Default steuert alle Rollouts des Portals ohne eigenen Override; ein Override nur seinen Artikel.
  const betroffenDefault = (portal: string) => offen.filter((r) => r.portal === portal
    && !overrides.some((o) => o.portal === portal && o.stock_item_id === r.stock_item_id))
  const betroffenOverride = (portal: string, stockItemId: number | null, sku: string) => offen.filter((r) => r.portal === portal
    && (stockItemId !== null ? r.stock_item_id === stockItemId : gleicheSku(r.sku, sku) || gleicheSku(r.kennung?.sku, sku)))

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['rollout-rules'] })

  // Alle Schreibwege durch dieselbe Amazon-Normalisierung — kein Pfad an der Härtung vorbei.
  const saveDefault = useMutation({
    mutationFn: ({ portal, payload }: { portal: string; payload: SetRulePayload }) =>
      setDefaultRule(portal, hardenAmazonLivePayload(portal, payload)),
    onSuccess: () => { invalidate(); setEditing(null); toast.success('Regel gespeichert') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen'),
  })
  const saveOverride = useMutation({
    mutationFn: ({ portal, sku, payload }: { portal: string; sku: string; payload: SetRulePayload }) =>
      setOverrideRule(portal, sku, hardenAmazonLivePayload(portal, payload)),
    onSuccess: () => { invalidate(); setEditing(null); setShowNewOverride(false); setNewSku(''); setNewPortal('base'); toast.success('Override gespeichert') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen'),
  })
  const removeOverride = useMutation({
    mutationFn: ({ portal, sku }: { portal: string; sku: string }) => deleteOverrideRule(portal, sku),
    onSuccess: () => { invalidate(); toast.success('Override entfernt') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Löschen fehlgeschlagen'),
  })
  const resetStrategy = useMutation({
    mutationFn: (sku: string) => setStrategy(sku, 'margin', 'both'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['strategy-exceptions'] })
      void queryClient.invalidateQueries({ queryKey: ['channel-preview'] })
      toast.success('Auf Standard zurückgesetzt')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Zurücksetzen fehlgeschlagen'),
  })
  const [newBeSku, setNewBeSku] = useState('')
  // Hinzufügen direkt aus der Übersicht — BULK-fähig: mehrere Nummern (Komma/Leerzeichen/
  // Zeilenumbruch getrennt, mit oder ohne DR-Praefix). Pro Eintrag: read-only Politik-Check
  // (channel-preview) mit DR-Fallback (2808 -> DR-2808), Grundartikel-SKUs abweisen
  // (Strategie wirkt nur auf Varianten/Kinder). EINE Sammel-Bestätigung, dann Teilanwendung:
  // auflösbare Artikel werden umgestellt, der Rest im Toast gemeldet.
  const addBreakEven = useMutation({
    mutationFn: async (input: string) => {
      const tokens = [...new Set(input.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean))]
      if (tokens.length === 0) return null
      const resolved: { sku: string; exkl: boolean }[] = []
      const failed: string[] = []
      for (const token of tokens) {
        let sku = token
        let preview = await channelPreview([sku])
        if (preview.rows.length === 0 && !/^dr-/i.test(token)) {
          sku = `DR-${token}`
          preview = await channelPreview([sku])
        }
        const row = preview.rows.length === 1 && preview.rows[0]!.sku === sku ? preview.rows[0]! : null
        if (!row) {
          if (preview.rows.length === 0) failed.push(`'${token}' nicht gefunden`)
          else failed.push(`'${sku}' ist ein Grundartikel mit Varianten (z.B. ${preview.rows[0]!.sku} angeben)`)
          continue
        }
        resolved.push({ sku, exkl: row.targets.some((t) => t.policy === 'exkl') })
      }
      if (resolved.length === 0) throw new Error(`Kein Artikel auflösbar: ${failed.join(' · ')}`)
      const exklSkus = resolved.filter((r) => r.exkl).map((r) => r.sku)
      const frage = [
        `${resolved.length} Artikel auf Break-Even umstellen (beide Kanäle, Selbstkostenpreis): ${resolved.map((r) => r.sku).join(', ')}.`,
        exklSkus.length > 0 ? `⚠ Kanal mit "Plus Versand" (exkl) — Break-Even wirkt dort NICHT: ${exklSkus.join(', ')}.` : '',
        failed.length > 0 ? `Übersprungen (nicht auflösbar): ${failed.join(' · ')}.` : '',
        'Fortfahren? (Feintuning je Kanal in der Preis-Kalkulation)',
      ].filter(Boolean).join('\n')
      if (!window.confirm(frage)) return null
      const applied: string[] = []
      const writeErrors: string[] = []
      for (const r of resolved) {
        try {
          await setStrategy(r.sku, 'break_even', 'both')
          applied.push(r.sku)
        } catch {
          writeErrors.push(`'${r.sku}' Speichern fehlgeschlagen`)
        }
      }
      return { applied, skipped: [...failed, ...writeErrors] }
    },
    onSuccess: (res) => {
      if (res === null) return
      void queryClient.invalidateQueries({ queryKey: ['strategy-exceptions'] })
      void queryClient.invalidateQueries({ queryKey: ['channel-preview'] })
      setNewBeSku('')
      if (res.applied.length > 0) toast.success(`Break-Even-Ausnahme angelegt: ${res.applied.join(', ')}`)
      if (res.skipped.length > 0) toast.error(`Übersprungen: ${res.skipped.join(' · ')}`)
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Anlegen fehlgeschlagen'),
  })

  if (isLoading) return <div className="py-6 text-center text-ink-2">Lade…</div>

  return (
    <div className="space-y-4">
      <KontoAufschlagPanel />
      <Panel title="Portal-Regeln (Defaults)" subtitle="Gelten für alle Rollouts des Portals ohne Artikel-Override.">
        {([true, false] as const).map(hasRule => {
          const portals = PORTALS.filter(p => Boolean(defaults.find(r => r.portal === p)) === hasRule)
          const table = (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead><tr className="text-left text-xs text-muted"><th>Portal</th><th>Status</th><th>Regel</th><th>Betroffen</th><th /></tr></thead>
                <tbody className="divide-y divide-line-2">
                  {portals.map((portal) => {
                    const rule = defaults.find((r) => r.portal === portal)
                    return (
                      <RuleRowGroup key={portal} label={portal} rule={rule} betrieb={status?.betrieb ?? null}
                        offenAnzahl={offen.filter((r) => r.portal === portal).length}
                        editing={editing === (rule?.id ?? -1) || (!rule && editing === -PORTALS.indexOf(portal) - 100)}
                        onEdit={() => setEditing(rule?.id ?? -PORTALS.indexOf(portal) - 100)}
                        onCancel={() => setEditing(null)}
                        onSave={(payload) => saveDefault.mutate({ portal, payload })}
                        saving={saveDefault.isPending} betroffen={betroffenDefault(portal)} />
                    )
                  })}
                </tbody>
              </table>
            </div>
          )
          return hasRule ? <div key="rules">{table}</div> : (
            <details key="empty" className="mt-4">
              <summary className="cursor-pointer text-sm focus-visible:outline-2 focus-visible:outline-primary">
                Weitere Portale ohne Regel ({portals.length})
              </summary>
              {table}
            </details>
          )
        })}
      </Panel>

      <Panel
        title="Artikel-Overrides"
        subtitle="Regel für einzelne Artikel — überstimmt den Portal-Default."
        actions={!showNewOverride && (
          <Button size="sm" onClick={() => setShowNewOverride(true)}>
            <Plus className="size-3.5" /> Override anlegen
          </Button>
        )}
      >
        {showNewOverride && (
          <div className="border border-primary/40 rounded-md p-4 mb-4 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-1">Varianten-SKU</div>
                <Input value={newSku} onChange={(e) => setNewSku(e.target.value)} placeholder="z.B. 2808" className="w-40 h-9" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-1">Portal</div>
                <select value={newPortal} onChange={(e) => setNewPortal(e.target.value)}
                  className="h-9 px-2 text-sm border border-line rounded bg-bg">
                  {PORTALS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setShowNewOverride(false); setNewSku(''); setNewPortal('base') }}>Abbrechen</Button>
            </div>
            <RuleForm
              key={newPortal}
              isNew
              portal={newPortal}
              initial={toPayload(defaults.find((r) => r.portal === newPortal) ?? ({ ...DEFAULT_PAYLOAD, id: 0, portal: newPortal, stock_item_id: null, sku: null, updated_at: '' } as unknown as RolloutRule))}
              saving={saveOverride.isPending}
              betroffen={betroffenOverride(newPortal, null, newSku)}
              onSave={(payload) => {
                if (!newSku.trim()) { toast.error('SKU fehlt'); return }
                saveOverride.mutate({ portal: newPortal, sku: newSku.trim(), payload })
              }}
            />
          </div>
        )}
        {overrides.length === 0 ? (
          <div className="py-4 text-center text-muted text-sm">Keine Overrides.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted text-left">
                <th className="pb-2">Artikel</th>
                <th className="pb-2">Portal</th>
                <th className="pb-2">Aktiv</th>
                <th className="pb-2">Modus</th>
                <th className="pb-2">Regel</th>
                <th className="pb-2 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-2">
              {overrides.map((r) => (
                <Fragment key={r.id}>
                  <tr className="hover:bg-panel-2/40">
                    <td className="py-2 font-mono">{r.sku}</td>
                    <td className="py-2">{r.portal}</td>
                    <td className="py-2">{r.is_enabled ? 'ja' : 'nein'}</td>
                    <td className="py-2"><ModeBadge rule={r} /><AdapterBadge rule={r} /></td>
                    <td className="py-2 text-xs text-ink-2">{ruleSummary(r)}</td>
                    <td className="py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(editing === r.id ? null : r.id)}>
                        Bearbeiten
                      </Button>
                      <Button size="sm" variant="ghost" className="text-coral hover:text-coral"
                        onClick={() => { if (window.confirm(`Override ${r.sku}/${r.portal} löschen?`)) removeOverride.mutate({ portal: r.portal, sku: r.sku! }) }}>
                        <Trash2 className="size-3" />
                      </Button>
                    </td>
                  </tr>
                  {editing === r.id && (
                    <tr>
                      <td colSpan={6}>
                        <RuleForm portal={r.portal} initial={toPayload(r)} saving={saveOverride.isPending}
                          betroffen={betroffenOverride(r.portal, r.stock_item_id, r.sku ?? '')}
                          onSave={(payload) => saveOverride.mutate({ portal: r.portal, sku: r.sku!, payload })} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel
        title="Break-Even-Ausnahmen"
        subtitle="Varianten mit Break-Even-Strategie. Alle übrigen Artikel laufen automatisch über die Margen-Kalkulation."
      >
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-1">
              Varianten-SKUs (mehrere: Komma/Leerzeichen, ohne DR- möglich)
            </div>
            <Input
              value={newBeSku}
              onChange={(e) => setNewBeSku(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && newBeSku.trim() && !addBreakEven.isPending) addBreakEven.mutate(newBeSku.trim()) }}
              placeholder="z.B. 2808, 3588"
              className="w-72 h-9"
            />
          </div>
          <Button
            size="sm"
            disabled={!newBeSku.trim() || addBreakEven.isPending}
            onClick={() => addBreakEven.mutate(newBeSku.trim())}
          >
            <Plus className="size-3.5" /> Als Break-Even markieren
          </Button>
          <div className="basis-full text-xs text-muted">
            Tipp: Mit Preis-Vorschau je Kanal geht das auch im Grundartikel → Tab „Preise" (Karte „Preis-Strategie &amp; Rollout").
          </div>
        </div>
        {exceptions.length === 0 ? (
          <div className="py-4 text-center text-muted text-sm">Keine Ausnahmen — alle Artikel nutzen die Standard-Kalkulation.</div>
        ) : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-line-2">
              {exceptions.map((ex) => (
                <tr key={ex.stock_item_id} className="hover:bg-panel-2/40">
                  <td className="py-2 font-mono">{ex.sku}</td>
                  <td className="py-2 text-xs text-muted">{ex.name}</td>
                  <td className="py-2">
                    {ex.amazon_break_even && (
                      <span className="mr-1 px-1.5 py-0.5 rounded bg-panel-2 text-[10px] font-mono">amazon</span>
                    )}
                    {ex.base_break_even && (
                      <span className="px-1.5 py-0.5 rounded bg-panel-2 text-[10px] font-mono">base</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant="ghost"
                      onClick={() => { if (window.confirm(`${ex.sku} auf Standard (Marge) zurücksetzen?`)) resetStrategy.mutate(ex.sku) }}>
                      Auf Standard zurücksetzen
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}

const portalErklaerung: Record<string, string> = {
  base: 'DR-Grundpreis; gilt für eBay, Kaufland, Shopify, Metro ohne eigenen Preis',
  ebay: 'Eigener DR-eBay-Preis oder eBay direkt',
  amazon: 'Eigener Preis über SP-API oder DR',
  shopify: 'Eigener Shopify-Preis', kaufland: 'Eigener Kaufland-Preis', metro: 'Eigener Metro-Preis',
}

function regelStatus(rule: RolloutRule | undefined, betrieb: Betrieb | null): { text: string; klasse: string } {
  if (!rule?.is_enabled) return { text: 'Aus', klasse: 'bg-panel-2 text-muted' }
  if (rule.mode !== 'live') return { text: 'Nur Protokoll', klasse: 'bg-panel-2 text-muted' }
  if (betrieb?.live_writes && !betrieb.kill_aktiv) return { text: 'Schreibt live', klasse: 'bg-success-soft text-success' }
  return { text: 'LIVE eingestellt – Schreibfreigabe aus', klasse: 'bg-warn-soft text-warn' }
}

/** Default-Zeile pro Portal: Zeile + optional aufgeklapptes Formular. */
function RuleRowGroup({ label, rule, editing, onEdit, onCancel, onSave, saving, betroffen, betrieb, offenAnzahl }: {
  betrieb: Betrieb | null
  offenAnzahl: number
  label: string
  rule: RolloutRule | undefined
  editing: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: (p: SetRulePayload) => void
  saving: boolean
  betroffen: readonly RolloutStatus[]
}) {
  const status = regelStatus(rule, betrieb)
  return (
    <>
      <tr data-portal={label} className="hover:bg-panel-2/40 align-top">
        <td className="py-2">
          <div className="font-semibold">{portalText(label)}</div>
          <div className="text-xs text-muted">{portalErklaerung[label] ?? 'Portalpreis nach eigener Regel'}</div>
        </td>
        <td className="py-2">
          <span className={`rounded px-2 py-1 text-xs ${status.klasse}`}>{status.text}</span>
          {rule && <AdapterBadge rule={rule} />}
        </td>
        <td className="py-2 text-xs text-ink-2">
          {rule ? <>
            <div>Schritt: {schrittKlartext(rule)}</div>
            <div>Takt: {taktKlartext(rule)}</div>
            <div>Pause danach: {pauseKlartext(rule)}</div>
          </> : 'Portal-Default noch nicht angelegt'}
        </td>
        <td className="py-2 font-mono tabular-nums">{offenAnzahl}</td>
        <td className="py-2 text-right">
          <Button size="sm" variant="ghost" onClick={editing ? onCancel : onEdit}>
            {editing ? 'Abbrechen' : 'Bearbeiten'}
          </Button>
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={5}>
            <RuleForm portal={label} initial={rule ? toPayload(rule) : DEFAULT_PAYLOAD} onSave={onSave} saving={saving} betroffen={betroffen} />
          </td>
        </tr>
      )}
    </>
  )
}
